import type { StudentResponseEntity } from '@/domains/students/student.entity';

/**
 * Use Case para filtrar estudiantes por año de última matrícula
 * Cumple SRP: Solo se encarga de filtrar por año válido
 *
 * Responsabilidad: Incluir solo estudiantes cuyo año de última matrícula
 * sea igual al año actual. Excluye registros con valor nulo.
 */
export class FilterStudentsByCurrentYearUseCase {
  /**
   * Obtiene el año actual del sistema
   * @returns Año actual (ej: 2025)
   */
  private getCurrentYear(): number {
    return new Date().getFullYear();
  }

  /**
   * Filtra estudiantes incluyendo solo aquellos cuyo año de última matrícula
   * sea igual al año actual. Excluye registros con ano_ultima_matricula nulo.
   *
   * @param students - Array de estudiantes a filtrar
   * @returns Array de estudiantes con ano_ultima_matricula === año actual
   *
   * @example
   * const students = [
   *   { ano_ultima_matricula: 2024, ... }, // Excluido
   *   { ano_ultima_matricula: 2025, ... }, // Incluido (si año actual es 2025)
   *   { ano_ultima_matricula: 2026, ... }, // Excluido
   *   { ano_ultima_matricula: null, ... }  // Excluido
   * ];
   * const filtered = execute(students); // Solo 2025
   */
  execute(students: StudentResponseEntity[]): StudentResponseEntity[] {
    if (!students || students.length === 0) {
      return [];
    }

    const currentYear = this.getCurrentYear();

    return students.filter(
      (student) =>
        student.ano_ultima_matricula !== null &&
        student.ano_ultima_matricula === currentYear,
    );
  }
}
